import React from 'react';
import { Button } from '../../../components/ui';
import { EyeIcon, TrashIcon, UploadCloudIcon } from '../../../components/ui/Icons';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import {
  deleteVesselStamp,
  uploadVesselStamp,
  useVesselStampBlobUrl,
} from './useData';

type Props = {
  vesselId: number;
  stampStorageKey?: string | null;
  stampOriginalFilename?: string | null;
  onChanged?: () => void;
  editable?: boolean;
};

/** Stamps are often red/ink on transparent — preview on light paper-like stage. */
const STAMP_STAGE =
  'flex items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-50';

export const VesselStampPanel: React.FC<Props> = ({
  vesselId,
  stampStorageKey,
  stampOriginalFilename,
  onChanged,
  editable = true,
}) => {
  const { colors } = useTheme();
  const { stampBlobUrl, loading } = useVesselStampBlobUrl(vesselId, stampStorageKey);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [viewOpen, setViewOpen] = React.useState(false);

  const onPick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadVesselStamp(vesselId, file);
      onChanged?.();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Upload failed';
      setError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const onRemove = async () => {
    setBusy(true);
    setError(null);
    try {
      await deleteVesselStamp(vesselId);
      onChanged?.();
    } catch {
      setError('Could not remove stamp');
    } finally {
      setBusy(false);
    }
  };

  const openView = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (stampBlobUrl) setViewOpen(true);
  };

  const viewModal = (
    <Modal isOpen={viewOpen} onClose={() => setViewOpen(false)} size="lg">
      <ModalHeader>
        <ModalTitle>{stampOriginalFilename || 'Vessel stamp'}</ModalTitle>
      </ModalHeader>
      <ModalContent>
        <div className={cn(STAMP_STAGE, 'min-h-[240px] w-full p-6')}>
          {stampBlobUrl ? (
            <img
              src={stampBlobUrl}
              alt={stampOriginalFilename || 'Stamp'}
              className="max-h-[70vh] max-w-full object-contain"
            />
          ) : null}
        </div>
      </ModalContent>
      <ModalFooter>
        <Button type="button" variant="secondary" size="sm" onClick={() => setViewOpen(false)}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );

  return (
    <>
      <div className={cn('rounded-xl border p-4', colors.border, colors.surface)}>
        <div className="mb-2 text-sm font-semibold">Vessel stamp / seal</div>
        <div className="flex flex-wrap items-start gap-4">
          <button
            type="button"
            title={stampBlobUrl ? 'View stamp' : undefined}
            disabled={!stampBlobUrl}
            onClick={openView}
            className={cn(
              STAMP_STAGE,
              'h-28 w-28 shrink-0',
              stampBlobUrl && 'cursor-pointer hover:ring-2 hover:ring-indigo-400/40',
            )}
          >
            {loading ? (
              <span className={cn('text-xs', colors.textMuted)}>…</span>
            ) : stampBlobUrl ? (
              <img
                src={stampBlobUrl}
                alt={stampOriginalFilename || 'Stamp'}
                className="h-full w-full object-contain p-2"
              />
            ) : (
              <span className={cn('px-2 text-center text-xs', colors.textMuted)}>No stamp</span>
            )}
          </button>
          <div className="min-w-0 flex-1 space-y-2">
            <p className={cn('text-xs', colors.textMuted)}>
              Official stamp used on forms (PNG, JPG, GIF, WebP, BMP, or SVG · max 5 MB)
              {stampOriginalFilename ? ` · ${stampOriginalFilename}` : ''}
            </p>
            <div className="flex flex-wrap items-center gap-1.5">
              {stampBlobUrl ? (
                <Button
                  type="button"
                  size="icon"
                  variant="secondary"
                  className="h-8 w-8"
                  title="View stamp"
                  aria-label="View stamp"
                  onClick={openView}
                >
                  <EyeIcon className="h-4 w-4" />
                </Button>
              ) : null}
              {editable ? (
                <>
                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp,image/bmp,image/svg+xml,.png,.jpg,.jpeg,.gif,.webp,.bmp,.svg"
                    className="hidden"
                    onChange={(e) => void onPick(e.target.files?.[0])}
                  />
                  <Button
                    type="button"
                    size="icon"
                    className="h-8 w-8"
                    disabled={busy}
                    title={stampStorageKey ? 'Replace stamp' : 'Upload stamp'}
                    aria-label={stampStorageKey ? 'Replace stamp' : 'Upload stamp'}
                    onClick={() => inputRef.current?.click()}
                  >
                    <UploadCloudIcon className="h-4 w-4" />
                  </Button>
                  {stampStorageKey ? (
                    <Button
                      type="button"
                      size="icon"
                      variant="danger"
                      className="h-8 w-8"
                      disabled={busy}
                      title="Remove stamp"
                      aria-label="Remove stamp"
                      onClick={() => void onRemove()}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </Button>
                  ) : null}
                </>
              ) : null}
            </div>
            {error ? <p className="text-xs text-red-600">{error}</p> : null}
          </div>
        </div>
      </div>
      {viewModal}
    </>
  );
};

export default VesselStampPanel;
